import { Component, ErrorInfo } from "react";

import { ErrorBoundaryProps } from "../../types/components.types";

import Button from "./Button";

interface ErrorBoundaryState {
  hasError: boolean;
}

// Catches errors thrown while rendering its children and shows a fallback in their place.
// Without a boundary, a single component throwing during render makes React unmount the whole app, leaving a blank page.
// This has to be a class component: React has no hook equivalent of getDerivedStateFromError/componentDidCatch.
// Note: boundaries only catch errors thrown during rendering. Errors in event handlers and async code (e.g. API calls) still need their own try/catch.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.level ?? "page"}]`, error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Clear the error once the content that failed has been replaced (e.g. the user navigated away or ran a new search).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  render() {
    const { children, level = "page", label = "this section" } = this.props;

    if (!this.state.hasError) return children;

    // Section: a small inline message, so the rest of the page (e.g. the search form) stays usable.
    if (level === "section") {
      return (
        <div className="alert alert-danger" role="alert" data-testid="error-boundary-section">
          Something went wrong while displaying {label}. The rest of the page is still working.
        </div>
      );
    }

    // Page: replaces the page content only, so the navbar stays available.
    if (level === "page") {
      return (
        <div
          className="d-flex justify-content-center p-3"
          role="alert"
          data-testid="error-boundary-page"
        >
          <div className="text-center bg-white p-4 p-md-5 rounded-3 shadow border">
            <h2 className="fw-bold mb-3">Something went wrong</h2>
            <p className="lead mb-4">
              This page hit an unexpected error. You can try again, or use the menu to go somewhere
              else.
            </p>
            <Button onClick={this.reset} testId="error-boundary-retry">
              Try again
            </Button>
          </div>
        </div>
      );
    }

    // App: the last line of defence, for errors outside any page (e.g. in the layout or routing).
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center p-3"
        role="alert"
        data-testid="error-boundary-app"
      >
        <div className="text-center bg-white p-4 p-md-5 rounded-3 shadow border">
          <h2 className="fw-bold mb-3">Something went wrong</h2>
          <p className="lead mb-4">Phoenix hit an unexpected error. Please reload the page.</p>
          <Button
            onClick={() => {
              window.location.reload();
            }}
            testId="error-boundary-reload"
          >
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
